/**
 * Timezone-aware date/time formatting for the whole portal.
 *
 * SpectraIQ standardises on Australian Eastern time; each dealership can pick
 * its own zone in Settings → Organization. The backend stores every timestamp
 * in UTC and hands them to the UI as ISO-8601 — this module renders them in the
 * organization's zone so a NSW booking and a WA booking each read in their own
 * local clock, no matter where the viewer sits.
 *
 * The active zone is a module-level value hydrated once at app start
 * (CurrentUserProvider, from GET /v1/me; CrmProvider also sets it) and updated
 * when the setting is saved. It defaults to Australia/Sydney so formatting is
 * correct even before hydration.
 *
 * ── Civil dates ──
 * Several screens (the diary, My Day, task due filters) need to reason about
 * "which day is this" rather than "which instant". Those use a *civil date*:
 * a JS Date whose LOCAL year/month/day carry the org-zone calendar date, and
 * whose time component is meaningless. `isOrgSameDay` already followed that
 * convention; `orgCivilToday` / `orgAddDays` / `orgStartOfWeek` / `orgDayRange`
 * below complete it.
 *
 * Do the arithmetic on civil dates, then convert to instants with
 * `orgDayRange` / `orgRange` at the API boundary. Doing it the other way
 * round — adding 7 * 86400000 ms to an instant — silently skips or repeats an
 * hour across a DST change, which is what put the calendar's fetch window out
 * of step with the grid it rendered.
 */

const DEFAULT_ZONE = 'Australia/Sydney';

let _orgZone = DEFAULT_ZONE;

/** Update the active organization timezone (IANA id). Ignores blanks. */
export function setOrgTimezone(zone) {
  if (zone && typeof zone === 'string') {
    _orgZone = zone.trim() || DEFAULT_ZONE;
  }
}

/** The active organization timezone (IANA id). */
export function getOrgTimezone() {
  return _orgZone;
}

/** Coerce an ISO string / epoch / Date into a Date, or null if unparseable. */
function toDate(value) {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DEFAULTS = {
  dateTime: { dateStyle: 'medium', timeStyle: 'short' },
  date:     { dateStyle: 'medium' },
  time:     { timeStyle: 'short' },
};

/**
 * Format a timestamp in the organization's timezone.
 * @param value ISO string, epoch ms, or Date
 * @param opts  Intl.DateTimeFormat options (timeZone is injected)
 * @param fallback returned when value is null/invalid (default '—')
 */
export function formatInOrgTz(value, opts = DEFAULTS.dateTime, fallback = '—') {
  const d = toDate(value);
  if (!d) return fallback;
  try {
    return new Intl.DateTimeFormat('en-AU', { timeZone: _orgZone, ...opts }).format(d);
  } catch {
    // Bad zone id — fall back to the platform default rather than throwing.
    return new Intl.DateTimeFormat('en-AU', { timeZone: DEFAULT_ZONE, ...opts }).format(d);
  }
}

/** Date + time, e.g. "15 Aug 2026, 9:30 am". */
export const formatDateTime = (v, fallback) => formatInOrgTz(v, DEFAULTS.dateTime, fallback);
/** Date only, e.g. "15 Aug 2026". */
export const formatDate     = (v, fallback) => formatInOrgTz(v, DEFAULTS.date, fallback);
/** Time only, e.g. "9:30 am". */
export const formatTime     = (v, fallback) => formatInOrgTz(v, DEFAULTS.time, fallback);

/**
 * Wall-clock parts of an instant **in the organization's timezone**, e.g.
 * `{ year: 2026, month: 8, day: 12, hour: 13, minute: 44 }`. Use this for
 * calendar layout (which row/column a block sits in) so positioning agrees
 * with the labels — never `Date.getHours()`, which is the viewer's browser
 * zone and drifts from the org zone.
 */
function partsInZone(d, zone) {
  const dtf = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const o = {};
  for (const p of dtf.formatToParts(d)) if (p.type !== 'literal') o[p.type] = p.value;
  return {
    year: +o.year, month: +o.month, day: +o.day,
    hour: (+o.hour) % 24, minute: +o.minute,
  };
}

export function partsInOrgTz(value) {
  const d = toDate(value);
  if (!d) return null;
  try { return partsInZone(d, _orgZone); }
  catch { return partsInZone(d, DEFAULT_ZONE); }
}

/** Fractional hour-of-day (0–24) of an instant in the org timezone, or null. */
export function orgHourFraction(value) {
  const p = partsInOrgTz(value);
  return p ? p.hour + p.minute / 60 : null;
}

/** True if `value` falls on the given civil date (a Date) in the org timezone. */
export function isOrgSameDay(value, dateObj) {
  const p = partsInOrgTz(value);
  return !!p
    && p.year === dateObj.getFullYear()
    && p.month === dateObj.getMonth() + 1
    && p.day === dateObj.getDate();
}

/**
 * Interpret a `YYYY-MM-DD` date + `HH:mm` time as a wall-clock reading **in the
 * organization's timezone** and return the matching UTC instant (Date), or null.
 *
 * The inverse of {@link partsInOrgTz}: a form that shows "12 Aug, 1:44 pm" to a
 * dealership on AEST must save 03:44Z, regardless of the browser's own zone.
 * Uses the standard offset-probe (guess the wall time as UTC, see how the zone
 * renders it, correct by the difference).
 */
export function orgWallClockToDate(dateStr, timeStr) {
  if (!dateStr) return null;
  // Accept a combined `datetime-local` value ("2026-08-12T13:44") in dateStr.
  if (timeStr == null && String(dateStr).includes('T')) {
    [dateStr, timeStr] = String(dateStr).split('T');
  }
  if (!timeStr) return null;
  const [y, mo, d] = String(dateStr).split('-').map(Number);
  const [h, mi] = String(timeStr).split(':').map(Number);
  if ([y, mo, d, h, mi].some((n) => Number.isNaN(n))) return null;

  const wallUTC = Date.UTC(y, mo - 1, d, h, mi, 0);

  // How far the org zone is ahead of UTC at a given instant.
  const offsetAt = (ms) => {
    let seen;
    try { seen = partsInZone(new Date(ms), _orgZone); }
    catch { seen = partsInZone(new Date(ms), DEFAULT_ZONE); }
    return Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour, seen.minute, 0) - ms;
  };

  const rendersAsRequested = (ms) => {
    let seen;
    try { seen = partsInZone(new Date(ms), _orgZone); }
    catch { seen = partsInZone(new Date(ms), DEFAULT_ZONE); }
    return seen.year === y && seen.month === mo && seen.day === d
        && seen.hour === h && seen.minute === mi;
  };

  // Two-pass probe. A single pass reads the offset at the *guessed* instant,
  // which is the wrong side of a DST transition for any wall time within an
  // offset's distance of the changeover — midnight on the changeover day being
  // the case that matters, since that is where every day and week boundary
  // sits. Probing again at the first guess and re-solving fixes it.
  const first = wallUTC - offsetAt(wallUTC);
  const second = wallUTC - offsetAt(first);

  if (first === second) return new Date(first);
  if (rendersAsRequested(second)) return new Date(second);
  if (rendersAsRequested(first)) return new Date(first);
  // Neither renders back: the wall time does not exist (spring-forward gap).
  // Move forward past the gap, which is what a person means by "9am" on a day
  // where 9am was skipped.
  return new Date(Math.max(first, second));
}

/** {@link orgWallClockToDate} as an ISO-8601 UTC string (or null). */
export function orgWallClockToISO(dateStr, timeStr) {
  const d = orgWallClockToDate(dateStr, timeStr);
  return d ? d.toISOString() : null;
}

/** Short label for the active zone, e.g. "AEST" / "AWST". */
export function orgTimezoneAbbrev(when = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: _orgZone, timeZoneName: 'short',
    }).formatToParts(when);
    return parts.find((p) => p.type === 'timeZoneName')?.value || _orgZone;
  } catch {
    return _orgZone;
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Civil-date helpers
 *
 * A civil date is a JS Date whose LOCAL y/m/d carry the org-zone calendar date.
 * Its time component is set to local noon so that a later `new Date(y, m, d)`
 * round-trip can never slip to the adjacent day on a zone with a midnight DST
 * transition (Lord Howe, and historically several others).
 * ──────────────────────────────────────────────────────────────────────────── */

const CIVIL_HOUR = 12;

/** Build a civil date from org-zone y/m/d numbers. */
export function orgCivil(year, month, day) {
  return new Date(year, month - 1, day, CIVIL_HOUR, 0, 0, 0);
}

/** The civil date an instant falls on, in the org timezone. */
export function orgCivilOf(value) {
  const p = partsInOrgTz(value);
  return p ? orgCivil(p.year, p.month, p.day) : null;
}

/** Today's civil date in the org timezone — not the browser's. */
export function orgCivilToday() {
  return orgCivilOf(new Date());
}

/**
 * Add (or subtract) whole days to a civil date.
 * Pure calendar arithmetic: never touches instants, so DST cannot bite.
 */
export function orgAddDays(civil, n) {
  const d = new Date(civil);
  d.setDate(d.getDate() + n);
  d.setHours(CIVIL_HOUR, 0, 0, 0);
  return d;
}

/** Monday of the week containing `civil`, as a civil date. */
export function orgStartOfWeek(civil) {
  const d = new Date(civil);
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  return orgAddDays(d, -dow);
}

/** `YYYY-MM-DD` for a civil date — the value an <input type="date"> expects. */
export function civilToISODate(civil) {
  if (!civil) return '';
  const y = civil.getFullYear();
  const m = String(civil.getMonth() + 1).padStart(2, '0');
  const d = String(civil.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse a `YYYY-MM-DD` string into a civil date, or null. */
export function isoDateToCivil(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split('-').map(Number);
  if ([y, m, d].some((n) => Number.isNaN(n))) return null;
  return orgCivil(y, m, d);
}

/**
 * The UTC instants bounding one org-local day, as ISO strings.
 * `to` is exclusive — the start of the following day.
 */
export function orgDayRange(civil) {
  const day = civilToISODate(civil);
  const next = civilToISODate(orgAddDays(civil, 1));
  return {
    fromISO: orgWallClockToISO(day, '00:00'),
    toISO: orgWallClockToISO(next, '00:00'),
  };
}

/**
 * The UTC instants bounding an inclusive span of org-local days.
 * `orgRange(mon, sun)` covers Monday 00:00 through the end of Sunday.
 */
export function orgRange(fromCivil, toCivil) {
  return {
    fromISO: orgDayRange(fromCivil).fromISO,
    toISO: orgDayRange(toCivil).toISO,
  };
}

/** Whole days between two civil dates (b - a). */
export function orgDaysBetween(a, b) {
  const ms = orgCivil(b.getFullYear(), b.getMonth() + 1, b.getDate())
           - orgCivil(a.getFullYear(), a.getMonth() + 1, a.getDate());
  return Math.round(ms / 86_400_000);
}

/** True when the civil date is today in the org timezone. */
export function isOrgToday(civil) {
  const t = orgCivilToday();
  return !!civil && !!t
    && civil.getFullYear() === t.getFullYear()
    && civil.getMonth() === t.getMonth()
    && civil.getDate() === t.getDate();
}

/**
 * Short relative label for a due date — "in 3h", "2d overdue", "today 2:00 pm".
 * Shared by the task table, My Day and the next-event chips so overdue reads
 * the same everywhere.
 */
export function relativeDue(value, now = Date.now()) {
  const d = toDate(value);
  if (!d) return { label: 'no due date', overdue: false };
  const diff = d.getTime() - now;
  const overdue = diff < 0;
  const mins = Math.round(Math.abs(diff) / 60_000);
  let label;
  if (mins < 1) label = 'now';
  else if (mins < 60) label = `${mins}m`;
  else if (mins < 60 * 24) label = `${Math.round(mins / 60)}h`;
  else label = `${Math.round(mins / (60 * 24))}d`;
  return {
    label: overdue ? `${label} overdue` : `in ${label}`,
    overdue,
    absolute: formatDateTime(d),
  };
}
